import styled from 'styled-components';

import { ThemeProp } from '~/theme/Themes';

export type BannerVariant = 'blue' | 'amber';

interface BannerContainerProps {
  $variant: BannerVariant;
}

// Variant colors are specific to this banner's design and have no theme token.
const BLUE_BG = 'rgba(0,82,204,0.08)';
const BLUE_COLOR = '#0052cc';
const AMBER_BG = '#fef1cd';
const AMBER_COLOR = '#644a02';

export const BannerContainer = styled.div<BannerContainerProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  /* Rendered inside InputContainer, above the input box, so it inherits the same
     horizontal inset/width as the input in both collapsed and expanded modes.
     Extra bottom padding + negative bottom margin let the input box overlap the
     banner's lower edge: square bottom corners are hidden and the input's rounded
     top reveals the banner color behind it (the notch in the design). */
  margin-bottom: -0.75rem;
  padding: 0.625rem 0.75rem 1.375rem;
  gap: 0.5rem;
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.xs};
  font-family: ${({ theme }: ThemeProp) => theme.fonts.primary};
  font-weight: ${({ theme }: ThemeProp) => theme.fontWeight.regular};
  background: ${({ $variant }) => ($variant === 'amber' ? AMBER_BG : BLUE_BG)};
  color: ${({ $variant }) => ($variant === 'amber' ? AMBER_COLOR : BLUE_COLOR)};
`;

export const BannerLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.375rem;
  flex: 1;
`;

export const BannerRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

export const IconBadge = styled.div<BannerContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  flex-shrink: 0;
  color: ${({ $variant }) => ($variant === 'amber' ? AMBER_COLOR : BLUE_COLOR)};

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

export const BannerText = styled.span`
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.xs};
  font-family: ${({ theme }: ThemeProp) => theme.fonts.primary};
  font-weight: ${({ theme }: ThemeProp) => theme.fontWeight.regular};
  line-height: 1.4;
`;

export const LearnMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: ${({ theme }: ThemeProp) => theme.fontSize.xs};
  font-family: ${({ theme }: ThemeProp) => theme.fonts.primary};
  font-weight: ${({ theme }: ThemeProp) => theme.fontWeight.regular};
  text-decoration: underline;
  color: inherit;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }
`;

export const DismissButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  flex-shrink: 0;

  svg {
    width: 1rem;
    height: 1rem;
    stroke: currentColor;
  }

  &:hover {
    opacity: 0.8;
  }
`;
